import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router';
import { BannerCardList, BannerCardProps } from '@edu-frontend/modules/src/entities/user';
import { IconBook } from '@edu-frontend/modules/src/components/ui/Icon/ui/book';
import { Text } from '@edu-frontend/modules/src/components/ui/Text';
import { useStaticUrls } from 'platform-components';
import { useParams, generatePath } from 'react-router';
import Spin from 'platform-components/src/components/spin/spin';
import { DEFAULT_GRADE_INFORMATICS } from 'common.components/user/user.constants';
import { useTeacherMainPageCourseContainersQuery } from 'common.components/teacher-main-page/queries';
import { useGradeFromQueryOrTeam } from '../../../bundles/lab/utils/use-current-grade';

import './course-container-widget-v2__list.scss';

interface Props {
    /** грейд класса, имеет наивысший приоритет перед query и currentTeamGrade */
    grade?: number;

    /** предмет класа, имеет наивысший приоритет перед query  */
    subject?: string;

    className?: string;

    onItemClick?: () => void;
}

/** Отображает баннера курс-контейнеров */
export const CourseContainerWidgetV2__list = (props: Props) => {
    const history = useHistory();
    const intl = useIntl();
    const staticUrls = useStaticUrls();
    const { classId, subject: subjectFromQuery } = useParams<{ classId?: string; subject: string }>();
    const { className, grade, subject, onItemClick } = props;

    const gradeFromQueryOrTeam = useGradeFromQueryOrTeam(DEFAULT_GRADE_INFORMATICS);

    const currentGrade = grade ?? gradeFromQueryOrTeam;
    const currentSubject = subject ?? subjectFromQuery;

    const courseContainersQuery = useTeacherMainPageCourseContainersQuery({
        grade: currentGrade,
    });

    const { data, isLoading, isFetching } = courseContainersQuery;

    const items: BannerCardProps[] | undefined = useMemo(() => {
        if (data) {
            return data.course_container_info.map((courseContainer) => {
                const { slug } = courseContainer;

                const url = generatePath(
                    classId
                        ? staticUrls.labLibrarySubjectCollection
                        : staticUrls.labLibrarySubjectCommonCourseContainer,

                    {
                        classId,
                        subject: currentSubject ?? 'informatics',
                        containerSlug: slug,
                    }
                );

                const imgSrc = intl.formatMessage({ id: `courseModuleBanner.${slug}.img` });
                const defaultImgSrc = intl.formatMessage({ id: 'courseModuleBanner.defaultImg' });
                const currentImgSrc = imgSrc.includes('courseModuleBanner') ? defaultImgSrc : imgSrc;

                const header = intl.formatMessage({ id: `courseModuleBanner.${slug}.header` });
                const description = intl.formatMessage({ id: `courseModuleBanner.${slug}.description` });
                const onClick = () => {
                    onItemClick?.();
                    // передаем query параметры для external lms пользователей (МЭШ и т.д.)
                    history.push(`${url}${history.location.search}`);
                };

                return {
                    id: slug,
                    imgSrc: currentImgSrc,
                    header,
                    description,
                    buttonText: intl.formatMessage({
                        id: 'courseModuleBanner.buttonText',
                    }),
                    buttonIcon: <IconBook size="m" />,
                    buttonView: 'primary',
                    onClick,
                };
            });
        }
        return undefined;
    }, [data, classId, staticUrls, intl, history, currentSubject, onItemClick]);

    if (isLoading || isFetching) {
        return (
            <div className={'course-container-widget-v2__list__spin'}>
                <Spin />
            </div>
        );
    }

    if (!data) {
        return (
            <Text as="div" typography="bodyM">
                {intl.formatMessage({ id: 'courseContainer.widget.emptyContent' })}
            </Text>
        );
    }

    return (
        <div className={className}>
            <Text as="div" className="course-container-widget-v2__list__header" typography="titleM">
                {data.name}
            </Text>
            <Text
                as="div"
                className="course-container-widget-v2__list__description"
                typography="bodyM"
                weight="regular"
            >
                {data.description}
            </Text>
            <BannerCardList className="course-container-widget-v2__list__container" items={items} />
        </div>
    );
};
